import './ResultsPanel.css';
import './AiContent.css';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '../../components/ui/Badge/Badge';
import { IoMdRefresh } from "react-icons/io";
import { IoCheckmarkCircleOutline, IoPlayCircleOutline } from 'react-icons/io5';
import { MdDownload, MdContentCopy, MdCheck } from 'react-icons/md';
import { RiAlertFill } from "react-icons/ri";
import { RUN_STATUS, getLastProcessStepId } from '../../context/ContentContext';
import { ImStop, ImPencil } from "react-icons/im";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { FaRegCircleCheck } from "react-icons/fa6";
import { marked } from 'marked';
import {
  buildSingleStepDocxBlob,
  buildWorkflowFullDocxBlob,
  collectPrincipalWordExportSections,
} from '../../utils/workflowFullDocx';

/** Steps where per-step Word download is hidden (start has no output; download has its own actions). */
const STEP_WORD_DOWNLOAD_EXCLUDE = new Set(['start', 'download']);

function sanitizeStepFilenamePart(labelOrId) {
  const s = String(labelOrId || 'step')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return (s || 'step').slice(0, 48);
}

export const InjectGeneratedHTML = ({ htmlContent }) => {
  const hostRef = useRef(null);

  useEffect(() => {
    if (hostRef.current) {
      let shadowRoot = hostRef.current.shadowRoot;
      if (!shadowRoot) shadowRoot = hostRef.current.attachShadow({ mode: 'open' });
      shadowRoot.innerHTML = htmlContent;
    }
  }, [htmlContent]);

  return <div ref={hostRef} />;
};

// const PodcastPlayer = ({ url }) => {
//   const audioRef = useRef(null);
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [currentTime, setCurrentTime] = useState(0);
//   const [duration, setDuration] = useState(0);

//   const togglePlay = () => {
//     if (!audioRef.current) return;
//     if (audioRef.current.paused) {
//       audioRef.current.play().catch(err => console.error("Erro ao dar play:", err));
//       setIsPlaying(true);
//     } else {
//       audioRef.current.pause();
//       setIsPlaying(false);
//     }
//   };

//   return (
//     <div className="podcast-player">
//       <audio 
//         ref={audioRef} 
//         src={url} 
//         onTimeUpdate={() => setCurrentTime(audioRef.current.currentTime)} 
//         onLoadedMetadata={() => setDuration(audioRef.current.duration)}
//         onEnded={() => setIsPlaying(false)}
//         preload="metadata"
//       />
//       <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
//         <button onClick={togglePlay} type="button" className="podcast-button">
//           {isPlaying ? <FaPause size={18} /> : <FaPlay size={18} style={{marginLeft: '3px'}} />}
//         </button>
//         <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
//           <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '11px' }}>
//             <span>{Math.floor(currentTime / 60)}:{(currentTime % 60).toFixed(0).padStart(2, '0')}</span>
//             <span>{Math.floor(duration / 60)}:{(duration % 60).toFixed(0).padStart(2, '0')}</span>
//           </div>
//           <input
//             type="range"
//             min="0"
//             max={duration || 0}
//             step="0.1"
//             value={currentTime}
//             onChange={(e) => {
//               const time = parseFloat(e.target.value);
//               audioRef.current.currentTime = time;
//               setCurrentTime(time);
//             }}
//             className="input-podcast"
//             style={{ background: `linear-gradient(to right, #0047AB ${(currentTime / duration) * 100}%, #334155 0%)` }}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

/** Toolbar: teal “Copy text” + per-step Word “Download results”, or main-output download on the download node. */
const StepActionsToolbar = ({
  message,
  currentStep,
  stepLabel,
  config,
  onDownloadMainOutput,
  mainDownloadOutputDisabled,
}) => {
  const [wordBusy, setWordBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const hasMessage = !!String(message || '').trim();
  const useMainOutputDownload = typeof onDownloadMainOutput === 'function';
  const canWordExport =
    hasMessage &&
    currentStep &&
    !STEP_WORD_DOWNLOAD_EXCLUDE.has(currentStep) &&
    !useMainOutputDownload;

  const handleCopy = async () => {
    if (!hasMessage) return;
    const textarea = document.createElement('textarea');
    textarea.value = message;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    document.body.removeChild(textarea);
  };

  const handleWordDownload = async () => {
    if (!canWordExport || wordBusy) return;
    setWordBusy(true);
    try {
      const blob = await buildSingleStepDocxBlob({
        raw: message,
        stepLabel: stepLabel || currentStep,
        config,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mia-step-${sanitizeStepFilenamePart(stepLabel || currentStep)}-${Date.now()}.docx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Step Word export failed:', e);
    } finally {
      setWordBusy(false);
    }
  };

  if (!hasMessage) return null;

  return (
    <div className="run-results-panel__step-actions" role="toolbar" aria-label="Step result actions">
      <button
        type="button"
        className="run-results-panel__step-teal-btn"
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy text'}
      >
        <span className="run-results-panel__step-teal-btn__icon" aria-hidden>
          {copied ? <MdCheck size={18} /> : <MdContentCopy size={18} />}
        </span>
        <span>Copy text</span>
      </button>
      {useMainOutputDownload ? (
        <button
          type="button"
          className="run-results-panel__step-teal-btn"
          onClick={() => {
            if (mainDownloadOutputDisabled) return;
            onDownloadMainOutput();
          }}
          disabled={!!mainDownloadOutputDisabled}
          title="Download main output"
        >
          <span className="run-results-panel__step-teal-btn__icon" aria-hidden>
            <MdDownload size={18} />
          </span>
          <span>Download results</span>
        </button>
      ) : null}
      {canWordExport ? (
        <button
          type="button"
          className="run-results-panel__step-teal-btn"
          onClick={handleWordDownload}
          disabled={wordBusy}
          title="Download Word for this step"
        >
          <span className="run-results-panel__step-teal-btn__icon" aria-hidden>
            <MdDownload size={18} />
          </span>
          <span>{wordBusy ? 'Preparing…' : 'Download results'}</span>
        </button>
      ) : null}
    </div>
  );
};

const GeneratedInlineCopyButton = ({ message }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const textarea = document.createElement('textarea');
    textarea.value = message;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    document.body.removeChild(textarea);
  };

  return (
    <button
      className="run-results-panel__copy-button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy content'}
      type="button"
    >
      {copied ? <MdCheck /> : <MdContentCopy />}
    </button>
  );
};

const GeneratedResults = ({ results, config, omitInlineCopy = false }) => {
  if (!results?.message) return null;

  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(results.message || '');

  const renderMarkdown = () => {
    const rawHtml = marked.parse(results.message);
    return <div className="ai-content" dangerouslySetInnerHTML={{ __html: rawHtml }} />;
  };

  return (
    <div className={`run-results-panel__generated ${omitInlineCopy ? 'run-results-panel__generated--no-inline-copy' : ''}`}>
      {!omitInlineCopy && <GeneratedInlineCopyButton message={results.message} />}
      {hasHtmlTags ? (
        <InjectGeneratedHTML htmlContent={results.message} />
      ) : (
        <div className="ai-content"><pre>{renderMarkdown()}</pre></div>
      )}
      {results.errorMessage && <p>{results.errorMessage}</p>}
    </div>
  );
};

const ProcessingResultsView = ({ results, hasResults, currentStep, stepLabel, config }) => (
  <>
    {hasResults ? (
      <div className="run-results-panel__step-flat">
        <StepActionsToolbar
          message={results?.message}
          currentStep={currentStep}
          stepLabel={stepLabel}
          config={config}
        />
        <div className="run-results-panel__step-flat-scroll">
          <GeneratedResults results={results} config={config} omitInlineCopy />
        </div>
      </div>
    ) : (
      <div className="run-results-panel__step-flat run-results-panel__step-flat--skeleton-only">
        <div className="run-results-panel__empty-preview">
          <div className="skeleton-loader">
            <div className="skeleton-line"></div>
            <div className="skeleton-line"></div>
            <div className="skeleton-line" style={{ width: '80%' }}></div>
          </div>
        </div>
      </div>
    )}
  </>
);

const FeedbackOnResultsView = ({
  results,
  isRunning,
  improvementPrompt,
  onGenerateMaterials,
  onPromptChange,
  onImprove,
  config,
  currentStep,
  stepLabel,
}) => {
  const MAX_LENGTH = 2000;
  const remainingChars = MAX_LENGTH - (improvementPrompt || '').length;

  return (
    <div className="run-results-panel__download-view">
      <div className="run-results-panel__step-flat">
        <StepActionsToolbar
          message={results?.message}
          currentStep={currentStep}
          stepLabel={stepLabel}
          config={config}
        />
        <div className="run-results-panel__step-flat-scroll">
          <GeneratedResults results={results} config={config} omitInlineCopy />
        </div>

        <div className="run-results-panel__feedback-inline">
          <h4 className="run-results-panel__feedback-inline-title">Feedback on results</h4>
          <div className="run-results-panel__feedback-grid">
            <div className="run-results-panel__feedback-option">
              <p className="run-results-panel__feedback-text">I&apos;m satisfied</p>
              <button
                type="button"
                className="button button--gradient-orange buttons-pattern run-results-panel__feedback-gradient-btn"
                onClick={onGenerateMaterials}
                aria-label="Finish process and download results"
              >
                <span className="run-results-panel__feedback-gradient-btn__icon" aria-hidden>
                  <FaRegCircleCheck size={18} />
                </span>
                <span className="button__text run-results-panel__feedback-gradient-btn__text">
                  Finish process
                  <br />
                  and download results
                </span>
              </button>
            </div>

            <hr className="run-results-panel__divider-vertical run-results-panel__feedback-column-rule" />

            <div className="run-results-panel__feedback-option">
              <p className="run-results-panel__feedback-text">I&apos;m NOT satisfied</p>

              <div className="not-satisfied-panel">
                <div className="run-results-panel__not-satisfied-compose">
                  <div className="run-results-panel__not-satisfied-field">
                    <textarea
                      className="run-results-panel__textarea"
                      value={improvementPrompt ?? ''}
                      onChange={onPromptChange}
                      placeholder="Write here what you need to improve the results."
                      rows={2}
                      disabled={isRunning}
                      maxLength={MAX_LENGTH}
                    />
                  </div>
                  <button
                    type="button"
                    className="button button--secondary buttons-pattern button-improve run-results-panel__feedback-improve-btn"
                    onClick={onImprove}
                    aria-label="Improve results"
                  >
                    <span className="run-results-panel__feedback-improve-btn__icon" aria-hidden>
                      <IoMdRefresh size={18} />
                    </span>
                    <span className="button__text run-results-panel__feedback-improve-btn__text">
                      Improve
                      <br />
                      results
                    </span>
                  </button>
                </div>
                <p className="run-results-panel__char-counter">
                  {remainingChars}/{MAX_LENGTH} characters remaining
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DownloadResultsView = ({
  results,
  hasResults,
  onDownload,
  onDownloadFull,
  canDownloadFull,
  fullExportBusy,
  config,
}) => (
  <div className="run-results-panel__download-view">
    <div className="run-results-panel__step-flat">
      <StepActionsToolbar
        message={results?.message}
        currentStep="download"
        stepLabel="Download"
        config={config}
        onDownloadMainOutput={onDownload}
        mainDownloadOutputDisabled={!hasResults}
      />
      <div className="run-results-panel__step-flat-scroll">
        {hasResults ? (
          <GeneratedResults results={results} config={config} omitInlineCopy />
        ) : (
          <div className="run-results-panel__empty-preview run-results-panel__empty-preview--in-flat">
            <p>Your generated materials will be displayed here once available.</p>
          </div>
        )}
      </div>
    </div>
    <div className="run-results-panel__download-actions">
      <strong>Feedback on results</strong><br />
      <div className="run-results-approved">
        <strong><IoCheckmarkCircleOutline size={20} /> Approved!</strong>
      </div>
    </div>
    <hr className="run-results-panel__divider" />
    <div className="run-results-panel__download-actions">
      <strong>Download full content</strong>
      <p className="run-results-panel__feedback-text">
        All set!
        <br />
        Download the full workflow as a Word file from this run.
      </p>
      <div className="button-group run-results-panel__download-button-row run-results-panel__download-button-row--single">
        <button
          type="button"
          className="button button--primary buttons-pattern"
          onClick={onDownloadFull}
          disabled={!canDownloadFull || fullExportBusy}
        >
          <span className="button__icon button__icon--left"><MdDownload /></span>
          <span className="button__text">{fullExportBusy ? 'Preparing…' : 'Download full content'}</span>
        </button>
      </div>
    </div>
    <div className="run-results-panel__alert">
      <RiAlertFill size={34} />
      <div>
        <strong>Attention!</strong>
        <p className="run-results-panel__feedback-text">
          It's important to approve all generated content in Content Factory before publishing it to any external audience. 
          This ensures all information is accurate and meets Bayer's standards.
        </p>
      </div>
    </div>
  </div>
);

const makeFile = (data, type, name) => {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
};

const ConfigBadges = ({ config }) => {
  const items = [
    config?.bayerBrand,
    config?.brand || config?.data?.brand || config?.product || config?.data?.product,
    config?.selectedFormatLabel || (config?.data?.selectedFormatLabel === 'Email' ? 'E-mail' : config?.data?.selectedFormatLabel),
    config?.outputLanguage || config?.data.outputLanguage || config?.Output_language || config?.data.Output_language,
  ].filter(Boolean);

  return !items.length ? null : (
    <div className="run-results-panel__badges">
      {items.map((i, idx) => <Badge key={idx} variant="info" size="small">{i}</Badge>)}
    </div>
  );
};

const ErrorNotice = ({ msg }) => (
  <div className="run-results-panel__error">
    <strong>Generation failed.</strong>
    <p>{msg}</p>
  </div>
);

export const ResultsPanel = ({
  steps, isRunning, status, config, currentStep, results, error, improvementPrompt,
  onGenerateMaterials, onImprove, onPromptChange, onStopRun, onContinueRun, onReturnToFeedback, onEditRun,
}) => {
  const [fullExportBusy, setFullExportBusy] = useState(false);
  const hasResults = !!results?.message;
  const lastProcessStepId = getLastProcessStepId(steps || []);
  const showEmbeddedFeedback =
    !!lastProcessStepId &&
    status === RUN_STATUS.WAITING &&
    !error &&
    (currentStep === lastProcessStepId || currentStep === 'feedback');
  const headingStepId =
    currentStep === 'feedback' && lastProcessStepId ? lastProcessStepId : currentStep;
  const headingStepData = steps?.find((s) => s.id === headingStepId);
  const isDownloadStep = currentStep === 'download';
  const isStartStep = currentStep === 'start';
  const isStopped = status === RUN_STATUS.STOPPED;
  const currentStepData = steps?.find((s) => s.id === currentStep);

  const getContentToDisplay = () => {
    if (isStartStep) return null;
    
    const currentStepData = steps?.find(s => s.id === currentStep);
    
    if (currentStepData?.message) {
      return { message: currentStepData.message };
    }
    
    if (results?.allStepContents?.[currentStep]) {
      return { message: results.allStepContents[currentStep] };
    }
    
    return results;
  };

  const contentToDisplay = getContentToDisplay();
  const hasContentToDisplay = !!contentToDisplay?.message;

  const principalExportCount = collectPrincipalWordExportSections(steps, results?.allStepContents, config).length;
  const canDownloadFull = principalExportCount > 0;

  const handleDownloadFull = async () => {
    if (!canDownloadFull) return;
    setFullExportBusy(true);
    try {
      const blob = await buildWorkflowFullDocxBlob({
        steps,
        allStepContents: results?.allStepContents,
        config,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mia-workflow-full-${Date.now()}.docx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Full docx export failed:', e);
    } finally {
      setFullExportBusy(false);
    }
  };

  const handleDownload = () => {
    if (!hasResults) return;

    // const isPodcast = config?.selectedFormat === 'article';
    // if (isPodcast) {
    //   const match = results.message.match(/https?:\/\/[^\s)"'<>]+/g);
    //   if (match && match[0]) {
    //     const link = document.createElement('a');
    //     link.href = match[0];
    //     link.download = '';
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    //     return;
    //   }
    // }

    const isEmail = config?.selectedFormat === 'email';
    const ext = isEmail ? 'html' : 'txt';
    const mime = isEmail ? 'text/html' : 'text/plain';
    const name = `mia-${config?.selectedFormat || 'content'}-${Date.now()}.${ext}`;
    makeFile(results.message, `${mime};charset=utf-8`, name);
  };

  const renderContent = () => {
    if (showEmbeddedFeedback) {
      return (
        <FeedbackOnResultsView
          results={contentToDisplay}
          isRunning={isRunning}
          improvementPrompt={improvementPrompt}
          onPromptChange={onPromptChange}
          onImprove={onImprove}
          onGenerateMaterials={onGenerateMaterials}
          config={config}
          currentStep={headingStepId}
          stepLabel={headingStepData?.label || headingStepId}
        />
      );
    }
    if (isDownloadStep) {
      return (
        <DownloadResultsView
          results={contentToDisplay}
          hasResults={hasContentToDisplay}
          onDownload={handleDownload}
          onDownloadFull={handleDownloadFull}
          canDownloadFull={canDownloadFull}
          fullExportBusy={fullExportBusy}
          config={config}
        />
      );
    }
    if (error) return null;
    return (
      <ProcessingResultsView
        results={contentToDisplay}
        hasResults={hasContentToDisplay}
        isRunning={isRunning}
        improvementPrompt={improvementPrompt}
        onPromptChange={onPromptChange}
        onImprove={onImprove}
        onGenerateMaterials={onGenerateMaterials}
        currentStep={currentStep}
        stepLabel={currentStepData?.label || currentStep}
        config={config}
      />
    );
  };

  return (
  <div className="run-results-panel">
      <header className="run-results-panel__header">
          <h3>Results</h3>

      </header>
      {isRunning && (
          <button className="button button--danger buttons-pattern-small" onClick={onStopRun}>
              <span className="button__icon button__icon--left">
                          <ImStop  size={20} />
                  </span>
              <span className="button__text ">Stop Run</span>
          </button>
      )}

      {isStopped && (
          <>
              <div className="run-results-panel__buttons">
                  <button className="button button--edit-run buttons-pattern-small" onClick={onEditRun}>
                      <span className="button__icon button__icon--left">
                          <ImPencil size={20} />
                      </span>
                      <span className="button__text">Edit run</span>
                  </button>
                  <button className="button button--run buttons-pattern-small" onClick={onContinueRun}>
                      <span className="button__icon button__icon--left">
                          <IoPlayCircleOutline size={25} />
                      </span>
                      <span className="button__text">Run</span>
                  </button>
              </div>
          </>
      )}

      <ConfigBadges config={config} />
      {isStopped ? (
        <>
          <hr className="run-results-panel__divider" />
          <div>
            <strong>Content Creation</strong>
            <p className="run-panel__stopped-message"><AiOutlineExclamationCircle size={20} />Run Stopped</p>
          </div>
        </>
      ) : (
        <>
          <div className="run-results-panel__badges-rule" role="presentation" />
          <h4 className="run-results-panel__node-heading">{headingStepData?.label || headingStepId}</h4>
          {error && <ErrorNotice msg={error} />}
          {renderContent()}
        </>
      )}
    </div>
  );
};